import { FC, useEffect, useRef, useState } from 'react';
import { useDashboardById } from '../../hooks/useDashboardById';
import { useUserState } from '../../hooks/context/useUserState';
import ReportCard from './components/ReportCard/ReportCard';
import './Dashboard.css';
import Row from '../Row/Row';
import Column from '../Column/Column';
import { usePrevious } from '../../hooks/usePrevious';

interface DashboardProps {
  id: string;
  onReadyChange?: (ready: boolean) => void;
}

const Dashboard: FC<DashboardProps> = ({ id, onReadyChange }) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [rows, setRows] = useState<any[][]>([[]]);
  const [fetchedReports, setFetchedReports] = useState<string[]>([]);
  const [allReportsFetched, setAllReportsFetched] = useState(false);

  const dashboard = useDashboardById(id);
  const {
    state: { reports },
  } = useUserState();
  const prevAllReportsFetched = usePrevious(allReportsFetched);
  const refetchRimeoutRef = useRef<NodeJS.Timeout>(undefined);

  const reportsTimeoutLimit = 5000;

  const getAllReportsInRow = () => {
    if (!dashboard?.reports) {
      return null;
    }
    let screenSize = 'xs';
    if (windowWidth >= 1500) {
      screenSize = 'xl';
    } else if (windowWidth >= 1100) {
      screenSize = 'lg';
    } else if (windowWidth >= 720) {
      screenSize = 'md';
    } else if (windowWidth >= 576) {
      screenSize = 'sm';
    }
    const rows: any[][] = [[]];
    let rowWidth = 0;
    for (
      let columnIndex = 0;
      columnIndex < dashboard.reports.length;
      columnIndex++
    ) {
      const column = dashboard.reports[columnIndex] as any;
      if (column.size[screenSize]) {
        const report = reports.find((report) => report.id === column.id);
        if (rowWidth + column.size[screenSize] <= 12) {
          rowWidth += column.size[screenSize];
          rows[rows.length - 1].push(report);
        } else {
          rowWidth = column.size[screenSize];
          rows.push([report]);
        }
      }
    }
    return rows;
  };

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    const showReportsTimeout = setTimeout(() => {
      setAllReportsFetched(true);
    }, reportsTimeoutLimit);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(showReportsTimeout);
      clearTimeout(refetchRimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (reports) {
      const reportRows = getAllReportsInRow();
      if (reportRows) {
        setRows(reportRows);
      }
    }
  }, [windowWidth, reports]);

  useEffect(() => {
    if (
      fetchedReports.length &&
      dashboard?.reports?.length &&
      fetchedReports.length === dashboard.reports.length
    ) {
      setAllReportsFetched(true);
    }
  }, [dashboard, fetchedReports]);

  useEffect(() => {
    if (!allReportsFetched && prevAllReportsFetched) {
      refetchRimeoutRef.current = setTimeout(
        () => setAllReportsFetched(true),
        reportsTimeoutLimit
      );
    }
  }, [allReportsFetched, prevAllReportsFetched]);

  const onReportDataFetched = (id) => {
    setFetchedReports((oldReports) => {
      return oldReports.includes(id) ? oldReports : [...oldReports, id];
    });
  };

  const onReportDataLoading = (id) => {
    if (fetchedReports.includes(id)) {
      setAllReportsFetched(false);
      setFetchedReports((oldReports) =>
        oldReports.filter((item) => item !== id)
      );
    }
  };
  const dashboardLoaded =
    dashboard && dashboard.reports && reports && reports.length;
  const ready = !!dashboardLoaded && allReportsFetched;

  useEffect(() => {
    onReadyChange?.(ready);
  }, [ready, onReadyChange]);

  return (
    <>
      {dashboardLoaded && (
        <Row className={'graph-list'}>
          {dashboard?.reports
            ?.sort((a, b) => (a.ordNumber || 0) - (b.ordNumber || 0))
            .map((report) => {
              const reportsRow = rows.find((row) =>
                row.find((column) => column?.id === report.id)
              );
              return (
                <Column
                  key={report.id}
                  xs={report.size?.xs?.toString()}
                  sm={report.size?.sm?.toString()}
                  md={report.size?.md?.toString()}
                  lg={report.size?.lg?.toString()}
                  xl={report.size?.xl?.toString()}
                >
                  <ReportCard
                    key={report.id}
                    id={report.id ?? ''}
                    reportsInRow={reportsRow}
                    windowWidth={windowWidth}
                    onReportDataFetched={onReportDataFetched}
                    onReportDataLoading={onReportDataLoading}
                    allReportsFetched={allReportsFetched}
                  />
                </Column>
              );
            })}
        </Row>
      )}
    </>
  );
};

export default Dashboard;
